import { useState, useEffect } from 'react';
import { fetchExportedResourcesFromService } from '../../services/Resources/fetchExportedResourcesService';
import type { ExportedResourcesResponse } from '../../types';
import { logger } from '../../utils/logger';

interface UseFetchExportedResourcesResult {
  exportedResources: ExportedResourcesResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch the actually exported resources for a migration
 * @param accessToken - User's authentication token
 * @param migrationId - Migration ID to fetch exported resources for
 * @returns Object containing exported resources data, loading state, error, and refetch function
 */
export function useFetchExportedResources(
  accessToken: string | null,
  migrationId: string | undefined
): UseFetchExportedResourcesResult {
  const [exportedResources, setExportedResources] = useState<ExportedResourcesResponse | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    if (!accessToken || !migrationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchExportedResourcesFromService(accessToken, migrationId);
      setExportedResources(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch exported resources');
      setError(error);
      logger.error('useFetchExportedResources error', { error: err, migrationId });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, migrationId]);

  return {
    exportedResources,
    loading,
    error,
    refetch: fetchData
  };
}
